import type { PricingFeatureText } from '@/cms/components/blocks/pricing/pricing-1/types';
import { parsePricingFeatureText } from '@/cms/components/blocks/pricing/shared/pricing-view-utils';
import type { CheckoutErrorTranslationKey } from '@/components/checkout/checkout-errors';
import {
  type CheckoutErrorCode,
  checkoutErrorCodes,
} from '@/payment/services/checkout/types';
import type { PricingCheckoutCopy } from './default-copy';

export type PricingCheckoutHostedCopyKey =
  | CheckoutErrorTranslationKey
  | 'button.redirecting'
  | 'providerPicker.stripe'
  | 'providerPicker.paypal';

export type PricingCheckoutHostedCopy = Record<
  PricingCheckoutHostedCopyKey,
  string
>;

const checkoutMessageKeyByErrorCode = {
  missing_required: 'missingRequired',
  plan_unavailable: 'planUnavailable',
  price_unavailable: 'priceUnavailable',
  package_unavailable: 'packageUnavailable',
  invalid_package_price: 'invalidPackagePrice',
  provider_unavailable: 'providerUnavailable',
  provider_not_enabled: 'providerNotEnabled',
  provider_not_supported: 'providerNotSupported',
  subscription_blocked: 'subscriptionBlocked',
  pending_checkout_blocked: 'pendingCheckoutBlocked',
  pending_checkout_recovery_failed: 'pendingCheckoutRecoveryFailed',
  checkout_timed_out: 'checkoutTimedOut',
  checkout_create_failed: 'createFailed',
} as const satisfies Record<
  CheckoutErrorCode,
  keyof PricingCheckoutCopy['checkoutMessages']
>;

export function buildPricingCheckoutErrorCopy(
  copy: Pick<PricingCheckoutCopy, 'checkoutMessages'>
): Record<CheckoutErrorCode, string> {
  return Object.fromEntries(
    checkoutErrorCodes.map((code) => [
      code,
      copy.checkoutMessages[checkoutMessageKeyByErrorCode[code]],
    ])
  ) as Record<CheckoutErrorCode, string>;
}

export function formatPricingCheckoutTemplate(
  template: string,
  values: Record<string, number | string>
) {
  return Object.entries(values).reduce(
    (message, [token, value]) =>
      message.replaceAll(`{${token}}`, String(value)),
    template
  );
}

export function formatCmsPricingCurrency(params: {
  amountCents: number;
  locale: string;
  currency: string;
}) {
  return new Intl.NumberFormat(params.locale, {
    style: 'currency',
    currency: params.currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(params.amountCents / 100);
}

export function createPricingFeatureTextFromCopy(params: {
  text: string;
  highlight?: string;
  values: Record<string, number | string>;
}): PricingFeatureText {
  const parsedText = parsePricingFeatureText(
    formatPricingCheckoutTemplate(params.text, params.values)
  );
  const text = `${parsedText.before}${parsedText.highlight}${parsedText.after}`;
  const highlight = params.highlight
    ? formatPricingCheckoutTemplate(params.highlight, params.values)
    : '';

  if (!highlight) {
    return {
      before: text,
      highlight: '',
      after: '',
    };
  }

  const start = text.toLocaleLowerCase().indexOf(highlight.toLocaleLowerCase());
  if (start < 0) {
    return {
      before: text,
      highlight: '',
      after: '',
    };
  }

  const end = start + highlight.length;
  return {
    before: text.slice(0, start),
    highlight: text.slice(start, end),
    after: text.slice(end),
  };
}

export function buildPricingFeatureTexts(params: {
  features: Array<{ text: string; highlight?: string }>;
  values: Record<string, number | string>;
}) {
  return params.features.map((feature) =>
    createPricingFeatureTextFromCopy({
      text: feature.text,
      highlight: feature.highlight,
      values: params.values,
    })
  );
}

export function buildPricingCheckoutHostedCopy(
  copy: PricingCheckoutCopy
): PricingCheckoutHostedCopy {
  const checkoutErrorCopy = buildPricingCheckoutErrorCopy(copy);

  return {
    'button.redirecting': copy.checkoutMessages.redirecting,
    'providerPicker.stripe': copy.paymentButtons.stripe.label,
    'providerPicker.paypal': copy.paymentButtons.paypal.label,
    'errors.missingRequired': checkoutErrorCopy.missing_required,
    'errors.providerUnavailable': checkoutErrorCopy.provider_unavailable,
    'errors.planUnavailable': checkoutErrorCopy.plan_unavailable,
    'errors.priceUnavailable': checkoutErrorCopy.price_unavailable,
    'errors.packageUnavailable': checkoutErrorCopy.package_unavailable,
    'errors.invalidPackagePrice': checkoutErrorCopy.invalid_package_price,
    'errors.subscriptionBlocked': checkoutErrorCopy.subscription_blocked,
    'errors.pendingCheckoutBlocked': checkoutErrorCopy.pending_checkout_blocked,
    'errors.pendingCheckoutRecoveryFailed':
      checkoutErrorCopy.pending_checkout_recovery_failed,
    'errors.checkoutTimedOut': checkoutErrorCopy.checkout_timed_out,
    'errors.providerNotEnabled': checkoutErrorCopy.provider_not_enabled,
    'errors.providerNotSupported': checkoutErrorCopy.provider_not_supported,
    'errors.createFailed': checkoutErrorCopy.checkout_create_failed,
  };
}
